import type { Rule } from 'antd/lib/form';

/**
 * Cognito rejects anything that is not a plain ASCII email with
 * `InvalidParameterException: Username should be an email`, so the client has to
 * be at least as strict as it is before submitting.
 */

/**
 * Zero-width and bidi control characters (U+200B..U+200F, U+2060, U+FEFF).
 * They survive `String.trim()` and the `[^\s@]` character class, so an address
 * pasted out of Outlook or Excel can look perfectly valid in the table and in
 * the server logs while still failing Cognito.
 */
const INVISIBLE_CHARACTERS = /[\u200B-\u200F\u2060\uFEFF]/g;

const EMAIL_PATTERN =
  /^[A-Za-z0-9!#$%&'*+/=?^_`{|}~-]+(?:\.[A-Za-z0-9!#$%&'*+/=?^_`{|}~-]+)*@(?:[A-Za-z0-9](?:[A-Za-z0-9-]*[A-Za-z0-9])?\.)+[A-Za-z]{2,}$/;

export const EMAIL_REQUIRED_ERROR = 'Email is required';
export const EMAIL_INVALID_ERROR = 'Invalid email';
export const EMAIL_DUPLICATED_ERROR = 'Duplicated email';
export const EMAIL_DOMAIN_ERROR = 'Email domain is not allowed';

/** Strips invisible characters and surrounding whitespace. */
export const sanitizeEmail = (email?: string): string =>
  (email || '').replace(INVISIBLE_CHARACTERS, '').trim();

export const isValidEmail = (email: string): boolean =>
  EMAIL_PATTERN.test(email);

/** Key to compare addresses by, so `A@b.com` and `a@b.com` count as the same. */
export const emailKey = (email?: string): string =>
  sanitizeEmail(email).toLowerCase();

type ValidationOptions = {
  /** Keys (see {@link emailKey}) already claimed by another row. */
  takenEmails?: Set<string>;
  domainWhitelist?: Array<string>;
};

/**
 * Returns the error to show for `email`, or `null` when it can be submitted.
 * The address is expected to have been through {@link sanitizeEmail} already.
 */
export const getEmailValidationError = (
  email: string,
  { takenEmails, domainWhitelist }: ValidationOptions = {}
): string | null => {
  if (!email) {
    return EMAIL_REQUIRED_ERROR;
  }

  if (!isValidEmail(email)) {
    return EMAIL_INVALID_ERROR;
  }

  if (takenEmails?.has(emailKey(email))) {
    return EMAIL_DUPLICATED_ERROR;
  }

  if (domainWhitelist && domainWhitelist.length > 0) {
    const emailDomain = email.split('@')[1].toLowerCase();
    const isWhitelisted = domainWhitelist.some(
      (domain) => domain.toLowerCase() === emailDomain
    );
    if (!isWhitelisted) {
      return EMAIL_DOMAIN_ERROR;
    }
  }

  return null;
};

/**
 * Field-level rule for an email input. It deliberately reuses
 * {@link isValidEmail} instead of antd's built-in `{ type: 'email' }`, whose
 * pattern is more permissive than Cognito: an address antd accepted could still
 * come back flagged, so the inline error and the row's Status column disagreed.
 *
 * Empty values are left to the `required` rule, and problems that need the whole
 * table (duplicates, domain whitelist) stay in the Status column.
 */
export const emailFormatRule: Rule = {
  validator: (_, value: string) => {
    const email = sanitizeEmail(value);
    if (!email || isValidEmail(email)) {
      return Promise.resolve();
    }
    return Promise.reject(new Error(EMAIL_INVALID_ERROR));
  },
};
